// React Query
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
// Utils
import queryString from 'api/utils/queryStringBuilder';
import { getRequest as get } from '../utils/HttpMethods';
import {
  createEntity,
  updateEntity,
  useGetQuery,
  useListQuery,
  useMutationQuery,
} from '../utils/queries';
// Interfaces
import { Site, isSite } from 'interfaces';
import { SiteMetricsResult, isSiteMetricsResult } from '@billing/validation';

export const siteQueryKeys = {
  all: ['sites'],
  list: (isAdmin: boolean) => [...siteQueryKeys.all, isAdmin],
  listByOrganization: (organizationID: string) => [
    ...siteQueryKeys.all,
    organizationID,
  ],
  listByProvider: (providerID: string) => [...siteQueryKeys.all, providerID],
  listByUser: (userID: string) => [...siteQueryKeys.all, userID],
  get: (id: string) => [...siteQueryKeys.all, id],
};

/**
 * @description Fetches a list of all sites.
 * @returns {Promise<Site[]>} A promise that resolves to an array of Site objects.
 */
const listSites = async (): Promise<Site[]> =>
  await get<Site[]>('QBilling', `/resource/site/all`);

/**
 * @description Hook to fetch a list of all sites.
 * @param {Object} params - Parameters for the hook.
 * @param {boolean} params.isAdmin - Indicates if the user is an admin.
 * @param {boolean} [params.displayNotification=false] - Whether to display notifications.
 * @returns {UseQueryResult<Site[], Error>} The result of the query.
 */
export const useListSites = ({
  isAdmin,
  displayNotification = false,
}: {
  isAdmin: boolean;
  displayNotification?: boolean;
}): UseQueryResult<Site[], Error> => {
  return useListQuery<Site>({
    queryKey: siteQueryKeys.list(isAdmin),
    fetchFunction: listSites,
    functionName: 'listSites',
    isEntity: isSite,
    params: isAdmin,
    enabled: !!isAdmin,
    displayNotification,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

/**
 * @description Fetches a site by its ID.
 * @param {string} id - The ID of the site.
 * @returns {Promise<Site | null>} A promise that resolves to a Site object or null.
 */
const getSite = async (id: string): Promise<Site | null> =>
  await get<Site>('QBilling', `/resource/site${queryString({ id })}`);

/**
 * @description Hook to fetch a site by its ID.
 * @param {string} id - The ID of the site.
 * @returns {UseQueryResult<Site, Error>} The result of the query.
 */
export const useGetSite = (id: string): UseQueryResult<Site, Error> => {
  return useGetQuery<Site>({
    queryKey: siteQueryKeys.get(id),
    fetchFunction: getSite,
    functionName: 'getSite',
    params: id,
    isEntity: isSite,
    enabled: !!id,
  });
};

/**
 * @description gets metric data for a site
 * @param {string} siteID - The ID of the site.
 * @returns {Promise<SiteMetricsResult>} A promise that resolves to a SiteMetricsResult object.
 */
const getSiteMetrics = async (siteID: string): Promise<SiteMetricsResult> =>
  await get<SiteMetricsResult>(
    'QBilling',
    `/resource/site/metrics${queryString({ siteID })}`
  );

/**
 * @description Hook to fetch a sites metrics data by its ID.
 * @param {string} siteID - The ID of the site.
 * @returns {UseQueryResult<SiteMetricsResult, Error>} The result of the query.
 */
export const useGetSiteMetrics = (
  siteID: string
): UseQueryResult<SiteMetricsResult, Error> => {
  return useGetQuery<SiteMetricsResult>({
    queryKey: siteQueryKeys.get(siteID),
    fetchFunction: getSiteMetrics,
    functionName: 'getSiteMetrics',
    params: siteID,
    isEntity: isSiteMetricsResult,
    enabled: !!siteID,
  });
};

/**
 * @description Fetches a list of sites by organization ID.
 * @param {string} organizationID - The ID of the organization.
 * @returns {Promise<Site[]>} A promise that resolves to an array of Site objects.
 */
const listSitesByOrganization = async (
  organizationID: string
): Promise<Site[]> =>
  await get<Site[]>(
    'QBilling',
    `/resource/site/organization${queryString({ organizationID })}`
  );

/**
 * @description Hook to fetch a list of sites by organization ID.
 * @param {Object} params - Parameters for the hook.
 * @param {string} params.orgID - The ID of the organization.
 * @param {boolean} [params.displayNotification=false] - Whether to display notifications.
 * @returns {UseQueryResult<Site[], Error>} The result of the query.
 */
export const useListSitesByOrganization = ({
  orgID,
  displayNotification = false,
}: {
  //todo change to organizationID
  orgID: string;
  displayNotification?: boolean;
}): UseQueryResult<Site[], Error> => {
  return useListQuery<Site>({
    queryKey: siteQueryKeys.listByOrganization(orgID),
    fetchFunction: listSitesByOrganization,
    functionName: 'listSitesByOrganization',
    params: orgID,
    isEntity: isSite,
    enabled: !!orgID,
    displayNotification,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

/**
 * @description Fetches a list of sites by provider ID.
 * @param {string} providerID - The ID of the provider.
 * @returns {Promise<Site[]>} A promise that resolves to an array of Site objects.
 */
const listSitesByProvider = async (providerID: string): Promise<Site[]> =>
  await get<Site[]>(
    'QBilling',
    `/resource/site/provider${queryString({ providerID })}`
  );

/**
 * @description Hook to fetch a list of sites by provider ID.
 * @param {Object} params - Parameters for the hook.
 * @param {string} params.providerID - The ID of the provider.
 * @param {boolean} [params.displayNotification=false] - Whether to display notifications.
 * @returns {UseQueryResult<Site[], Error>} The result of the query.
 */
export const useListSitesByProvider = ({
  providerID,
  displayNotification = false,
}: {
  providerID: string;
  displayNotification?: boolean;
}): UseQueryResult<Site[], Error> => {
  return useListQuery<Site>({
    queryKey: siteQueryKeys.listByProvider(providerID),
    fetchFunction: listSitesByProvider,
    functionName: 'listSitesByProvider',
    params: providerID,
    isEntity: isSite,
    enabled: !!providerID,
    displayNotification,
  });
};

/**
 * @description Fetches a list of sites by user ID.
 * @param {string} userID - The ID of the providers user.
 * @returns {Promise<Site[]>} A promise that resolves to an array of Site objects.
 */
const listSitesByProviderUser = async (userID: string): Promise<Site[]> =>
  await get<Site[]>(
    'QBilling',
    `/resource/site/user${queryString({ userID })}`
  );

/**
 * @description Hook to fetch a list of sites by providers user account.
 * @param {Object} params - Parameters for the hook.
 * @param {string} params.userID - The ID of the providers user account.
 * @param {boolean} [params.displayNotification=false] - Whether to display notifications.
 * @returns {UseQueryResult<Site[], Error>} The result of the query.
 */
export const useListSitesByProviderUser = ({
  userID,
  displayNotification = false,
}: {
  userID: string;
  displayNotification?: boolean;
}): UseQueryResult<Site[], Error> => {
  return useListQuery<Site>({
    queryKey: siteQueryKeys.listByProvider(userID),
    fetchFunction: listSitesByProviderUser,
    functionName: 'listSitesByProviderUser',
    params: userID,
    isEntity: isSite,
    enabled: !!userID,
    displayNotification,
  });
};

/**
 * @description Fetches a list of sites by user ID.
 * @param {string} userID - The ID of the providers user.
 * @returns {Promise<Site[]>} A promise that resolves to an array of Site objects.
 */
const listSitesByUser = async (userID: string): Promise<Site[]> =>
  await get<Site[]>('QBilling', `/resource/site/user/${userID}`);

/**
 * @description Hook to fetch a list of sites by providers user account.
 * @param {Object} params - Parameters for the hook.
 * @param {string} params.userID - The ID of the providers user account.
 * @param {boolean} [params.displayNotification=false] - Whether to display notifications.
 * @returns {UseQueryResult<Site[], Error>} The result of the query.
 */
export const useListSitesByUser = ({
  userID,
  displayNotification = false,
}: {
  userID: string;
  displayNotification?: boolean;
}): UseQueryResult<Site[], Error> => {
  return useListQuery<Site>({
    queryKey: siteQueryKeys.listByProvider(userID),
    fetchFunction: listSitesByUser,
    functionName: 'listSitesByUser',
    params: userID,
    isEntity: isSite,
    enabled: !!userID,
    displayNotification,
  });
};

/**
 * @description Creates a new site.
 * @param {Site} site - The site object to create.
 * @returns {Promise<Site | null>} A promise that resolves to the created Site object or null.
 */
const createSite = async (site: Site): Promise<Site | null> => {
  return createEntity<Site>({
    path: '/resource/site',
    entity: site,
    isEntity: isSite,
    functionName: 'createSite',
  });
};

/**
 * @description Hook to create a new site.
 * @returns {UseMutationResult<Site, Error>} The result of the mutation.
 */
export const useCreateSite = (): UseMutationResult<Site, Error> => {
  return useMutationQuery<Site>({
    mutationFn: createSite,
    successMessage: 'Site created successfully',
    errorMessage: 'Failed to create site',
    queryKey: siteQueryKeys.all,
  });
};

/**
 * @description Updates an existing site.
 * @param {Site} site - The site object to update.
 * @returns {Promise<Site>} A promise that resolves to the updated Site object.
 */
const updateSite = async (site: Site): Promise<Site> => {
  return updateEntity<Site>({
    path: '/resource/site',
    entity: site,
    isEntity: isSite,
    functionName: 'updateSite',
  });
};

/**
 * @description Hook to update an existing site.
 * @returns {UseMutationResult<Site, Error>} The result of the mutation.
 */
export const useUpdateSite = (): UseMutationResult<Site, Error> => {
  return useMutationQuery<Site>({
    mutationFn: updateSite,
    successMessage: 'Site updated successfully',
    errorMessage: 'Failed to update site',
    queryKey: siteQueryKeys.all,
  });
};
